import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, Download, Search, RefreshCw, Save, Music, Calendar, Link2, Eye,
  BarChart3, ChevronRight, ExternalLink, X, Shield,
} from 'lucide-react';
import { adminApi, setNoindex } from '../lib/adminApi';

const TABS = [
  { id: 'overview', label: 'Overview', icon: <BarChart3 size={16} /> },
  { id: 'leads',    label: 'Fan Leads', icon: <Eye size={16} /> },
  { id: 'shows',    label: 'Shows',    icon: <Calendar size={16} /> },
  { id: 'music',    label: 'Featured Music', icon: <Music size={16} /> },
  { id: 'links',    label: 'Links',    icon: <Link2 size={16} /> },
  { id: 'account',  label: 'Account',  icon: <Shield size={16} /> },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const AdminDashboardPage = ({ admin }) => {
  const [tab, setTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const undo = setNoindex();
    document.title = 'Dionlovelle · Admin';
    return undo;
  }, []);

  const handleLogout = async () => {
    try { await adminApi.logout(); } catch (e) { /* ignore */ }
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[color:var(--ink)] text-[color:var(--ivory)]" data-testid="admin-dashboard">
      {/* Admin nav bar (dedicated, not the public one) */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[color:var(--ink)]/85 border-b border-[color:var(--border-soft)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Shield size={18} className="text-[color:var(--gold)]" />
            <span className="font-display text-lg tracking-[0.28em] uppercase">Dionlovelle · Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-[color:var(--ivory-muted)]/60 max-w-[220px] truncate">
              {admin?.email}
            </span>
            <button
              onClick={handleLogout}
              className="ghost-button !py-2 !px-3 !text-[10px] !tracking-[0.22em]"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> SIGN OUT
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
        <ul className="flex gap-1 sm:gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="admin-tabs">
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <li key={t.id} className="shrink-0">
                <button
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs tracking-[0.18em] uppercase border transition-colors ${
                    active
                      ? 'bg-[color:var(--gold)]/15 border-[color:var(--gold)] text-[color:var(--gold)]'
                      : 'border-[color:var(--border-soft)] text-[color:var(--ivory-muted)]/70 hover:text-[color:var(--gold)]'
                  }`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  {t.icon}
                  {t.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {tab === 'overview' && <OverviewTab />}
        {tab === 'leads' && <LeadsTab />}
        {tab === 'shows' && <ShowsTab />}
        {tab === 'music' && <FeaturedMusicTab />}
        {tab === 'links' && <LinksTab />}
        {tab === 'account' && <AccountTab admin={admin} />}
      </main>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// OVERVIEW
// ──────────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub }) => (
  <div className="glass-card p-5">
    <p className="section-eyebrow mb-2">{label}</p>
    <p className="font-display text-4xl leading-none text-[color:var(--ivory)]">{value}</p>
    {sub && <p className="text-xs text-[color:var(--ivory-muted)]/60 mt-2">{sub}</p>}
  </div>
);

const OverviewTab = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminApi.overview();
      setData(d);
      setErr('');
    } catch (e) {
      setErr(e.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-sm opacity-60">Loading stats…</p>;
  if (err) return <p className="text-sm text-red-300">{err}</p>;

  const ev = data.event_counts || {};
  return (
    <div className="space-y-5" data-testid="overview-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total fan signups" value={data.total_leads} />
        <StatCard label="Last 7 days" value={data.last_7_days} />
        <StatCard label="Last 30 days" value={data.last_30_days} />
        <StatCard label="Signup rate" value={`${data.conversion_rate}%`} sub="Signups ÷ /live views" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="glass-card p-5">
          <p className="section-eyebrow mb-3">Top signup sources</p>
          {data.top_sources.length === 0 ? (
            <p className="text-sm opacity-60">No source data yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.top_sources.map((s) => (
                <li key={s.source} className="flex justify-between py-1.5 border-b border-[color:var(--border-soft)]">
                  <span className="truncate max-w-[70%]">{s.source}</span>
                  <span className="font-semibold text-[color:var(--gold)]">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="glass-card p-5">
          <p className="section-eyebrow mb-3">Button clicks (all-time)</p>
          <ul className="space-y-1.5 text-sm">
            {[
              ['Listen · All To Myself', ev.all_to_myself_click || 0],
              ['The Mint tickets', ev.mint_ticket_click || 0],
              ['Breaking Sound tickets', ev.breaking_sound_ticket_click || 0],
              ['Follow on Instagram', ev.instagram_click || 0],
              ['Book Dionlovelle (nav footer)', ev.booking_click || 0],
              ['View the EPK (nav footer)', ev.epk_click || 0],
            ].map(([k, v]) => (
              <li key={k} className="flex justify-between py-1 border-b border-[color:var(--border-soft)]">
                <span>{k}</span>
                <span className="font-semibold text-[color:var(--gold)]">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="glass-card p-5 text-sm">
        <p className="section-eyebrow mb-2">CSV Export</p>
        <p className="opacity-70 mb-2">Last export: {fmt(data.last_csv_export_at)}</p>
        <a
          href={adminApi.exportLeadsCsvUrl()}
          className="glow-button !py-2 !px-4 !text-[11px]"
          data-testid="overview-export-csv"
        >
          <Download size={14} /> DOWNLOAD LEADS CSV
        </a>
      </div>

      <button onClick={load} className="ghost-button !text-[10px] !py-2 !px-3">
        <RefreshCw size={12} /> REFRESH
      </button>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// LEADS
// ──────────────────────────────────────────────────────────────────────

const LeadsTab = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [campaign, setCampaign] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminApi.listLeads({ q, source, campaign, since, until });
      setRows(d.leads || []);
      setErr('');
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sources = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.utm_source) s.add(r.utm_source); if (r.source) s.add(r.source); });
    return Array.from(s).sort();
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="leads-content">
      <div className="glass-card p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <span className="section-eyebrow block mb-1">Search</span>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email"
                     className="dl-input pl-9 !py-2.5 !text-sm" data-testid="leads-search" />
            </div>
          </div>
          <div>
            <span className="section-eyebrow block mb-1">Source</span>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="dl-input !py-2.5 !text-sm" data-testid="leads-source">
              <option value="" className="bg-black">All</option>
              {sources.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
            </select>
          </div>
          <div>
            <span className="section-eyebrow block mb-1">Campaign</span>
            <input value={campaign} onChange={(e) => setCampaign(e.target.value)} className="dl-input !py-2.5 !text-sm" data-testid="leads-campaign" />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <span className="section-eyebrow block mb-1">Since</span>
              <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="dl-input !py-2.5 !text-sm" data-testid="leads-since" />
            </div>
            <div className="flex-1">
              <span className="section-eyebrow block mb-1">Until</span>
              <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="dl-input !py-2.5 !text-sm" data-testid="leads-until" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <button onClick={load} className="glow-button !py-2 !px-4 !text-[11px]" data-testid="leads-apply">
            <RefreshCw size={12} /> APPLY
          </button>
          <a href={adminApi.exportLeadsCsvUrl()} className="ghost-button !py-2 !px-4 !text-[11px]" data-testid="leads-export">
            <Download size={12} /> EXPORT CSV
          </a>
          <span className="ml-auto text-xs opacity-60">{rows.length} records</span>
        </div>
      </div>

      {err && <p className="text-sm text-red-300">{err}</p>}

      <div className="glass-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ivory-muted)]/60">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Source</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Campaign</th>
              <th className="text-left px-4 py-3">Signup</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-soft)]">
            {loading && <tr><td colSpan={6} className="px-4 py-6 opacity-60 text-center">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 opacity-60 text-center">No leads match your filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id || r.email} className="hover:bg-white/[0.02]" data-testid={`lead-row-${r.email}`}>
                <td className="px-4 py-3 font-medium">{r.first_name || '—'}</td>
                <td className="px-4 py-3 opacity-80">{r.email}</td>
                <td className="px-4 py-3 opacity-70 hidden md:table-cell">{r.utm_source || r.source || '—'}</td>
                <td className="px-4 py-3 opacity-70 hidden lg:table-cell">{r.utm_campaign || r.campaign || '—'}</td>
                <td className="px-4 py-3 opacity-70">{fmt(r.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setSelected(r)} className="text-[color:var(--gold)] hover:opacity-80" aria-label="View" data-testid={`lead-view-${r.email}`}>
                    <ChevronRight size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="lead-detail-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelected(null)} />
          <div className="relative w-full sm:max-w-md bg-neutral-950/95 border border-[color:var(--gold)]/30 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl">
            <button onClick={() => setSelected(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 flex items-center justify-center" aria-label="Close"><X size={16} /></button>
            <h3 className="font-display text-2xl mb-4">{selected.first_name}</h3>
            <dl className="text-sm space-y-2">
              {[
                ['Email', selected.email], ['Source', selected.utm_source || selected.source],
                ['Campaign', selected.utm_campaign || selected.campaign], ['UTM medium', selected.utm_medium],
                ['UTM content', selected.utm_content], ['Referrer', selected.referrer],
                ['Signed up', fmt(selected.created_at)], ['Last submit', fmt(selected.submitted_at)],
                ['Welcome email sent', selected.email_sent ? 'Yes' : 'No'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-[color:var(--border-soft)] py-1.5">
                  <dt className="opacity-60">{k}</dt><dd className="text-right truncate max-w-[60%]">{v || '—'}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// SHOWS
// ──────────────────────────────────────────────────────────────────────

const useSiteConfig = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const load = async () => {
    setLoading(true);
    try { setCfg(await adminApi.getSiteConfig()); setErr(''); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return { cfg, setCfg, loading, err, reload: load };
};

const saveSiteConfig = async (cfg) => {
  const payload = {
    shows: cfg.shows.map((s, i) => ({ ...s, order: s.order ?? i + 1 })),
    featured_music: cfg.featured_music,
    links: cfg.links,
  };
  await adminApi.saveSiteConfig(payload);
};

const ShowsTab = () => {
  const { cfg, setCfg, loading, err, reload } = useSiteConfig();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (loading) return <p className="opacity-60">Loading…</p>;
  if (err) return <p className="text-red-300">{err}</p>;

  const updateShow = (idx, key, val) => {
    const shows = [...cfg.shows];
    shows[idx] = { ...shows[idx], [key]: val };
    setCfg({ ...cfg, shows });
  };
  const move = (idx, dir) => {
    const shows = [...cfg.shows];
    const j = idx + dir;
    if (j < 0 || j >= shows.length) return;
    [shows[idx], shows[j]] = [shows[j], shows[idx]];
    shows.forEach((s, i) => (s.order = i + 1));
    setCfg({ ...cfg, shows });
  };
  const submit = async () => {
    setSaving(true);
    try { await saveSiteConfig(cfg); setSaved(true); setTimeout(() => setSaved(false), 2000); await reload(); }
    catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="shows-content">
      {cfg.shows.map((s, i) => (
        <div key={s.id} className="glass-card p-5" data-testid={`show-editor-${s.id}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/40 text-[color:var(--gold)] flex items-center justify-center text-xs font-semibold">{i + 1}</span>
            <h4 className="font-display text-xl flex-1">{s.venue || 'New show'}</h4>
            <button onClick={() => move(i, -1)} disabled={i === 0} className="ghost-button !text-[10px] !py-1.5 !px-3 disabled:opacity-40">↑</button>
            <button onClick={() => move(i, 1)} disabled={i === cfg.shows.length - 1} className="ghost-button !text-[10px] !py-1.5 !px-3 disabled:opacity-40">↓</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ['venue', 'Venue name'],
              ['date_display', 'Show date (public label)'],
              ['location', 'Location / city'],
              ['ticket_url', 'Ticket URL'],
              ['cta', 'Button label'],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <span className="section-eyebrow block mb-1">{label}</span>
                <input value={s[k] || ''} onChange={(e) => updateShow(i, k, e.target.value)}
                       className="dl-input !py-2.5 !text-sm" data-testid={`show-${s.id}-${k}`} />
              </label>
            ))}
            <label className="block">
              <span className="section-eyebrow block mb-1">Status</span>
              <select value={s.status || 'confirmed'} onChange={(e) => updateShow(i, 'status', e.target.value)}
                      className="dl-input !py-2.5 !text-sm" data-testid={`show-${s.id}-status`}>
                <option value="confirmed" className="bg-black">Confirmed (public)</option>
                <option value="draft" className="bg-black">Draft (hidden)</option>
              </select>
            </label>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <button onClick={submit} disabled={saving} className="glow-button !py-2.5 !px-5 !text-[11px]" data-testid="shows-save">
          {saving ? 'SAVING…' : (<><Save size={14} /> SAVE SHOWS</>)}
        </button>
        {saved && <span className="text-xs text-emerald-300">Saved ✓</span>}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// FEATURED MUSIC
// ──────────────────────────────────────────────────────────────────────

const FeaturedMusicTab = () => {
  const { cfg, setCfg, loading, err, reload } = useSiteConfig();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (loading) return <p className="opacity-60">Loading…</p>;
  if (err) return <p className="text-red-300">{err}</p>;

  const update = (key, val) => setCfg({ ...cfg, featured_music: { ...cfg.featured_music, [key]: val } });

  const submit = async () => {
    setSaving(true);
    try {
      // If spotify_url is a valid Spotify album URL, auto-extract embed id
      const m = cfg.featured_music.spotify_url?.match(/spotify\.com\/(?:album|track)\/([A-Za-z0-9]+)/);
      if (m) update('spotify_embed_id', m[1]);
      await saveSiteConfig(cfg);
      setSaved(true); setTimeout(() => setSaved(false), 2000); await reload();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const fm = cfg.featured_music;
  return (
    <div className="glass-card p-6 space-y-4" data-testid="music-content">
      <h4 className="font-display text-2xl">Featured song</h4>
      <label className="block">
        <span className="section-eyebrow block mb-1">Song title</span>
        <input value={fm.title} onChange={(e) => update('title', e.target.value)} className="dl-input" data-testid="music-title" />
      </label>
      <label className="block">
        <span className="section-eyebrow block mb-1">Short description</span>
        <textarea rows={3} value={fm.blurb} onChange={(e) => update('blurb', e.target.value)} className="dl-input" data-testid="music-blurb" />
      </label>
      <label className="block">
        <span className="section-eyebrow block mb-1">Spotify URL</span>
        <input value={fm.spotify_url} onChange={(e) => update('spotify_url', e.target.value)} className="dl-input" data-testid="music-spotify-url" />
      </label>
      <label className="block">
        <span className="section-eyebrow block mb-1">Spotify embed ID</span>
        <input value={fm.spotify_embed_id} onChange={(e) => update('spotify_embed_id', e.target.value)} className="dl-input" data-testid="music-embed-id" />
      </label>
      <label className="block">
        <span className="section-eyebrow block mb-1">Artwork image URL (public /path)</span>
        <input value={fm.cover_image || ''} onChange={(e) => update('cover_image', e.target.value)} className="dl-input" placeholder="/dionlovelle-hero.jpg" />
      </label>
      <label className="block">
        <span className="section-eyebrow block mb-1">Primary CTA wording</span>
        <input value={fm.primary_cta || 'LISTEN ON SPOTIFY'} onChange={(e) => update('primary_cta', e.target.value)} className="dl-input" />
      </label>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={submit} disabled={saving} className="glow-button !py-2.5 !px-5 !text-[11px]" data-testid="music-save">
          {saving ? 'SAVING…' : (<><Save size={14} /> SAVE FEATURED MUSIC</>)}
        </button>
        {saved && <span className="text-xs text-emerald-300">Saved ✓</span>}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// LINKS
// ──────────────────────────────────────────────────────────────────────

const LinksTab = () => {
  const { cfg, setCfg, loading, err, reload } = useSiteConfig();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (loading) return <p className="opacity-60">Loading…</p>;
  if (err) return <p className="text-red-300">{err}</p>;

  const update = (key, val) => setCfg({ ...cfg, links: { ...cfg.links, [key]: val } });

  const submit = async () => {
    setSaving(true);
    try { await saveSiteConfig(cfg); setSaved(true); setTimeout(() => setSaved(false), 2000); await reload(); }
    catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const links = cfg.links;
  const fields = [
    ['spotify', 'Spotify (All To Myself)'],
    ['instagram', 'Instagram'],
    ['the_mint', 'The Mint tickets'],
    ['breaking_sound', 'Breaking Sound tickets'],
    ['booking', 'Booking page'],
    ['epk', 'EPK page'],
  ];

  return (
    <div className="glass-card p-6 space-y-4" data-testid="links-content">
      <h4 className="font-display text-2xl">Public links</h4>
      <p className="text-xs opacity-60">Empty URLs will be rejected. External links open in new tab automatically.</p>
      {fields.map(([k, label]) => (
        <label key={k} className="block" data-testid={`link-field-${k}`}>
          <span className="section-eyebrow block mb-1">{label}</span>
          <div className="flex gap-2">
            <input value={links[k] || ''} onChange={(e) => update(k, e.target.value)} className="dl-input flex-1" data-testid={`link-input-${k}`} />
            {links[k] && (
              <a href={links[k]} target="_blank" rel="noopener noreferrer"
                 className="ghost-button !py-2 !px-3 !text-[10px]" aria-label={`Open ${label}`}>
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        </label>
      ))}

      <div className="flex items-center gap-3 pt-2">
        <button onClick={submit} disabled={saving} className="glow-button !py-2.5 !px-5 !text-[11px]" data-testid="links-save">
          {saving ? 'SAVING…' : (<><Save size={14} /> SAVE LINKS</>)}
        </button>
        {saved && <span className="text-xs text-emerald-300">Saved ✓</span>}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// ACCOUNT — change password
// ──────────────────────────────────────────────────────────────────────

const AccountTab = ({ admin }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(''); setErr('');
    if (next.length < 12) { setErr('New password must be at least 12 characters.'); return; }
    if (next !== confirm) { setErr('New passwords do not match.'); return; }
    setBusy(true);
    try {
      await adminApi.changePassword(current, next);
      setMsg('Password updated.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="account-content">
      <div className="glass-card p-6">
        <p className="section-eyebrow mb-2">Signed in as</p>
        <p className="font-display text-2xl mb-2">{admin?.email}</p>
        <p className="text-xs opacity-60">Last sign-in: {fmt(admin?.last_login_at)}</p>
        <p className="text-xs opacity-60">Account created: {fmt(admin?.created_at)}</p>
      </div>

      <form onSubmit={submit} className="glass-card p-6 space-y-3" data-testid="account-change-password">
        <h4 className="font-display text-2xl mb-1">Change password</h4>
        <label className="block">
          <span className="section-eyebrow block mb-1">Current password</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className="dl-input" data-testid="acct-current-password" />
        </label>
        <label className="block">
          <span className="section-eyebrow block mb-1">New password (min 12)</span>
          <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className="dl-input" data-testid="acct-new-password" />
        </label>
        <label className="block">
          <span className="section-eyebrow block mb-1">Confirm</span>
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="dl-input" data-testid="acct-confirm-password" />
        </label>
        {err && <p className="text-sm text-red-300">{err}</p>}
        {msg && <p className="text-sm text-emerald-300">{msg}</p>}
        <button type="submit" disabled={busy} className="glow-button !py-2.5 !px-5 !text-[11px]" data-testid="acct-submit">
          {busy ? 'UPDATING…' : 'UPDATE PASSWORD'}
        </button>
      </form>
    </div>
  );
};

export default AdminDashboardPage;
